// Utility for consistent step numbering across the application

export interface StepObject {
  step: string;
  sources?: any[];
  inference?: string;
  is_expanded?: boolean;
  parent_step_index?: number;
  conversation_derived?: boolean;
  created_at?: string;
}

export function isStepObject(val: unknown): val is StepObject {
  return typeof val === 'object' && val !== null && 'step' in val;
}

export function getStepNumber(steps: (string | StepObject)[], currentIndex: number): string {
  const step = steps[currentIndex];
  const stepObj = isStepObject(step) ? step : null;
  
  // If this is an expanded/sub-step, find its parent and number accordingly
  if (stepObj?.is_expanded && stepObj?.parent_step_index !== undefined) {
    const parentIndex = stepObj.parent_step_index;
    
    // Calculate the logical step number of the parent step
    // Count how many non-expanded steps come before the parent
    const regularStepsBeforeParent = steps
      .slice(0, parentIndex)
      .filter(s => !isStepObject(s) || !s.is_expanded).length;
    
    const parentStepNumber = regularStepsBeforeParent + 1;
    
    // Find all expanded steps with the same parent that come before this one
    const expandedStepsWithSameParent = steps
      .slice(0, currentIndex)
      .filter((s, i) => {
        if (!isStepObject(s)) return false;
        return s.is_expanded && s.parent_step_index === parentIndex;
      });
    
    const substepIndex = expandedStepsWithSameParent.length + 1;
    return `${parentStepNumber}.${substepIndex}`;
  }
  
  // For regular steps, find the base number by counting non-expanded steps before this one
  const regularStepsBeforeThis = steps
    .slice(0, currentIndex)
    .filter(s => !isStepObject(s) || !s.is_expanded).length;
  
  return (regularStepsBeforeThis + 1).toString();
}

export function getStepDisplayProps(steps: (string | StepObject)[], currentIndex: number) {
  const step = steps[currentIndex];
  const stepObj = isStepObject(step) ? step : null;
  const isExpanded = stepObj?.is_expanded || false;
  
  return {
    number: getStepNumber(steps, currentIndex),
    isExpanded,
    stepText: typeof step === 'string' ? step : stepObj?.step || '',
    stepObject: stepObj,
    numberClassName: isExpanded ? 'bg-purple-600' : 'bg-electric-blue',
    containerClassName: isExpanded ? 'bg-purple-50 border-purple-200 ml-6' : 'bg-white border-gray-200',
    badgeClassName: isExpanded ? 'text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full font-medium' : ''
  };
}